import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import { isOfflineMode } from '@atlaskit/editor-plugin-connectivity/connectivityPluginType';
import { sendableSteps } from '@atlaskit/prosemirror-collab';
import { type Transaction } from '@atlaskit/editor-prosemirror/state';

import { usePageContentId } from '@confluence/page-context';
import { expVal } from '@confluence/feature-experiments';

import type { EditorOfflineStoragePlugin } from './editorOfflineStoragePluginType.ts';
import { StepsDatabase } from './stepsDatabase.ts';
import { useBroadcastChannel } from './broadcastChannel.ts';
import {
	createPlugin,
	editorOfflineStoragePluginKey,
	type EditorOfflineStorageDisabledReason,
} from './pm-plugins/main.ts';
import { restoreStepsAction } from './pm-plugins/actions.ts';
import { syncWithOfflineStorage } from './pm-plugins/syncWithOfflineStorage.ts';
import { useResyncOnline } from './ui/hooks/useResyncOnline.ts';
import { useSyncEnabled } from './ui/hooks/useSyncEnabled.ts';
import { useCreateOfflineVersion } from './ui/hooks/useCreateOfflineVersion.ts';
import {
	showVersionInsertionErrorFlag,
	hideVersionInsertionErrorFlag,
} from './ui/versionInsertionFlags.tsx';
import { fireOfflineStorageAnalyticsEvent } from './analytics.ts';

export const editorOfflineStoragePlugin: EditorOfflineStoragePlugin = ({ api, config }) => {
	const stepsDatabase = new StepsDatabase();

	return {
		name: 'editorOfflineStorage',
		pmPlugins() {
			return [
				{
					name: 'editorOfflineStoragePlugin',
					plugin: createPlugin(stepsDatabase),
				},
			];
		},
		getSharedState(state) {
			if (!state) {
				return undefined;
			}
			const { status, disabled, inactiveSyncing } =
				editorOfflineStoragePluginKey.getState(state) ?? {};
			const { version, steps } = sendableSteps(state) ?? {};

			return {
				version: version ?? 0,
				steps: steps ?? [],
				status: status ?? 'pending',
				disabled: disabled ?? false,
				inactiveSyncing: inactiveSyncing ?? false,
				contentId: stepsDatabase.currentContentId,
			};
		},
		usePluginHook: ({ editorView }) => {
			const [contentId] = usePageContentId();
			const disabledRef = useRef<EditorOfflineStorageDisabledReason | boolean>(false);
			const hasCheckedForVersionInsertionBackupRef = useRef(false);
			const intl = useIntl();

			usePluginStateEffect(api, ['editorOfflineStorage'], ({ editorOfflineStorageState }) => {
				disabledRef.current = editorOfflineStorageState?.disabled ?? false;
			});

			const { createVersion: createOfflineVersion, manualRetryCreateVersion } =
				useCreateOfflineVersion({
					contentId,
					api,
					stepsDatabase,
					flags: config?.flags ?? null,
					hasEditPermission: config?.hasEditPermission,
					shouldSkipOfflineVersionForUnpublishedDraft:
						config?.shouldSkipOfflineVersionForUnpublishedDraft,
				});

			useEffect(() => {
				if (contentId && !stepsDatabase.dbName && !disabledRef.current) {
					void stepsDatabase.init(contentId, api).then(async (initialized) => {
						if (!initialized) {
							return;
						}

						// Check and clear expired data before checking for backup
						await stepsDatabase.checkAndClearExpiredData();

						// Check for existing version insertion backup
						if (!hasCheckedForVersionInsertionBackupRef.current) {
							hasCheckedForVersionInsertionBackupRef.current = true;
							const versionInsertionBackup = await stepsDatabase.getVersionInsertionBackup();

							// Keep the backup for a later edit session, once the content has been published.
							if (
								versionInsertionBackup &&
								!config?.shouldSkipOfflineVersionForUnpublishedDraft &&
								(!expVal('cc_offline_version_history_controls', 'isEnabled', false) ||
									config?.hasEditPermission !== false)
							) {
								if (expVal('cc_offline_version_history_controls', 'isEnabled', false)) {
									fireOfflineStorageAnalyticsEvent({
										api,
										action: 'OFFLINE_EDITING_VERSION_INSERTION_ERROR_FLAG_SHOWN',
										manualRetryCount: 0,
									});
								}

								showVersionInsertionErrorFlag(
									config?.flags,
									intl,
									// onRetry: Call manual retry with stored content
									() => {
										void manualRetryCreateVersion(versionInsertionBackup.adfContent);
									},
									expVal('cc_offline_version_history_controls', 'isEnabled', false)
										? () => {
												void stepsDatabase
													.clear(true)
													.then((cleared) => {
														if (cleared) {
															hideVersionInsertionErrorFlag(config?.flags);
														}
													})
													.catch(() => {}); // StepsDatabase reports the error and keeps the flag visible.
											}
										: undefined,
									// onClose: Track when user dismisses the version insertion error flag
									() => {
										fireOfflineStorageAnalyticsEvent({
											api,
											action: 'OFFLINE_EDITING_VERSION_INSERTION_ERROR_FLAG_DISMISSED',
											manualRetryCount: 0,
										});
									},
									0,
								);
							}
						}
					});
				}
				// We need stepsDatabase in deps to detect when DB is no longer initialised on re-render, e.g. when page goes full-width.
			}, [contentId, stepsDatabase, api, intl, manualRetryCreateVersion]); // eslint-disable-line react-hooks/exhaustive-deps

			const broadcastUpdate = useBroadcastChannel({ api, editorView, stepsDatabase });

			/**
			 * This is to resync the document online once we move from offline -> online
			 */
			useResyncOnline({
				api,
				editorView,
				broadcastUpdate,
				stepsDatabase,
				contentId,
				flags: config?.flags ?? null,
				hasEditPermission: config?.hasEditPermission,
				shouldSkipOfflineVersionForUnpublishedDraft:
					config?.shouldSkipOfflineVersionForUnpublishedDraft,
			});

			/**
			 * See if it's safe to re-enable the plugin if it's been disabled due to the lock being held
			 */
			useSyncEnabled({
				api,
				stepsDatabase,
				disabledRef,
			});

			// Clean up the sync timeout on unmount
			// Otherwise its possible that it will fire with stale data after unmount
			useEffect(() => {
				return () => {
					syncWithOfflineStorage.cancel();
				};
			}, [contentId, editorView]);

			/**
			 * This is to handle the initial load of the editor and syncing it from the indexeddb
			 */
			usePluginStateEffect(
				api,
				['editorOfflineStorage', 'connectivity', 'collabEdit'],
				({ editorOfflineStorageState, connectivityState, collabEditState }) => {
					const { status, steps, version, disabled } = editorOfflineStorageState ?? {};

					if (disabledRef.current) {
						return;
					}

					// oxlint-disable-next-line no-restricted-properties -- Feature detection only; database access goes through the approved adapter.
					if (window?.indexedDB === undefined) {
						// If indexeddb is not available disable offline editing here
						if (!disabled) {
							api?.core.actions.execute(({ tr }: { tr: Transaction }) =>
								tr.setMeta(editorOfflineStoragePluginKey, {
									disabled: 'stepsDatabaseError',
								}),
							);
						}
						return;
					}

					// Both the restore (init) sync lock and the resync online (online <-> offline) sync lock are released here.
					// We confirm the steps have synced with NCS before clearing the database and releasing the lock.
					if (steps?.length === 0 && stepsDatabase.hasLock) {
						void stepsDatabase.clear(false).then(async (cleared) => {
							if (cleared) {
								if (stepsDatabase.lockPurpose === 'restore') {
									// After we've the steps confirmed with NCS, we update the doc and version in the database.
									await stepsDatabase.updateDocAndVersion(editorView).then((isUpdated) => {
										if (isUpdated) {
											// Notify other tabs to sync from the updated doc/version in IndexedDB.
											broadcastUpdate('restore');
										}
									});
								}
								await stepsDatabase.releaseSyncLock?.();
							}
						});
					}

					switch (status) {
						case 'pending':
							// Restore steps once collab edit plugin is initialised
							if (collabEditState?.initialised.collabInitialisedAt) {
								const tr: Transaction = editorView.state.tr;
								tr.setMeta('addToHistory', false);
								tr.setMeta(editorOfflineStoragePluginKey, {
									status: 'restoring',
								});
								editorView.dispatch(tr);
								void restoreStepsAction(api, stepsDatabase, editorView, 0, {
									createOfflineVersion,
								})(editorView.state.schema);
							}
							break;

						case 'restoring':
							// Do nothing until the steps are restored
							break;

						case 'ready':
							const isFocusedAndVisible =
								// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
								document.visibilityState === 'visible' && document.hasFocus();
							// If we are offline and we have steps to store, update the steps in the database
							if (
								isFocusedAndVisible &&
								steps &&
								version !== undefined &&
								isOfflineMode(connectivityState?.mode)
							) {
								void syncWithOfflineStorage({
									editorView,
									stepsDatabase,
									version,
									steps,
									broadcast: broadcastUpdate,
								});
							}
							break;

						case 'tooManyRetries':
							// Unable to restore steps - ensure we disable the offline editing session to be safe
							// in case there's something wrong with the DB.
							if (!disabled) {
								api?.core.actions.execute(({ tr }: { tr: Transaction }) =>
									tr.setMeta(editorOfflineStoragePluginKey, {
										disabled: 'tooManyRetries',
									}),
								);
							}
							break;

						default:
							break;
					}
				},
			);
		},
	};
};
